import { ActionTree } from 'vuex';
import { IDocumentTracking } from '../types/document-tracking';
import { IClickTracking } from '../types/click-tracking';
import { TrackingHelper } from '../helpers/tracking.helper';
import { IVideoTracking } from '../types/video-tracking';
import { IPageTracking } from '../types/page-tracking';
import { IVisitTracking } from '../types/visit-tracking';
import { ETrackingAction, ETrackingMutation, ITrackingState } from './tracking.types';
import { getDefaultState } from './tracking.constants';
import { RootState } from '@/store/store';
import { DocumentService } from '@/api/documents/services/document.service';
import { alertNamespace, EAlertAction } from '@/alert/store/alert.constants';
import { translate } from '@/helpers/translation.helper';

export const actions: ActionTree<ITrackingState, RootState> = {
  [ETrackingAction.SelectRecipientId]({ commit }, id: string) {
    commit(ETrackingMutation.SetSelectedRecipientId, id);
  },
  [ETrackingAction.ResetSelectedRecipientId]({ commit }) {
    commit(ETrackingMutation.SetSelectedRecipientId, getDefaultState().selectedRecipientId);
  },

  async [ETrackingAction.Load]({ commit, dispatch }, documentId: string) {
    commit(ETrackingMutation.SetLoading, true);
    commit(ETrackingMutation.SetLoaded, false);
    try {
      const tracking: IDocumentTracking = await DocumentService.loadTracking(documentId);
      dispatch(ETrackingAction.LoadSuccess, tracking);
    } catch (error) {
      dispatch(ETrackingAction.LoadFailed, error);
    }
  },
  [ETrackingAction.LoadSuccess]({ commit }, tracking: IDocumentTracking) {
    commit(ETrackingMutation.SetTracking, tracking);
    commit(ETrackingMutation.SetLoading, false);
    commit(ETrackingMutation.SetLoaded, true);
  },
  [ETrackingAction.LoadFailed]({ commit, dispatch }, error: Error) {
    commit(ETrackingMutation.SetLoading, false);
    commit(ETrackingMutation.SetLoaded, false);
    console.error('Could not load tracking for document', error);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-get-page-statistics'),
      {
        root: true,
      }
    );
  },

  [ETrackingAction.AddClickTracking]({ commit }, clickTracking: IClickTracking) {
    commit(ETrackingMutation.AddClickTracking, clickTracking);
  },
  [ETrackingAction.AddVideoTracking]({ commit }, videoTracking: IVideoTracking) {
    commit(ETrackingMutation.AddVideoTracking, videoTracking);
  },
  [ETrackingAction.AddPageTracking]({ commit, state }, pageTracking: IPageTracking) {
    const updatedPageTrackings: IPageTracking[] = TrackingHelper.getUpdatedPageTrackings(
      state.tracking.pageTrackings,
      pageTracking
    );
    commit(ETrackingMutation.SetPageTrackings, updatedPageTrackings);
  },

  [ETrackingAction.AddVisitTimeTracking]({ commit }, trackVisitUpdate: IVisitTracking) {
    commit(ETrackingMutation.AddVisitTimeTracking, trackVisitUpdate);
  },

  [ETrackingAction.Reset]({ commit }) {
    commit(ETrackingMutation.SetState, getDefaultState());
  },
};
